import { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Trash2, Play, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import SlackChannelPicker from '../components/SlackChannelPicker';
import SortableItem from '../components/SortableItem';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const withUid = (t) => ({ ...t, __uid: t.__uid || uid() });

export default function PackagesPage() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pkgName, setPkgName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [frequency, setFrequency] = useState('weekly');
  const [sequential, setSequential] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [trigger, setTrigger] = useState('scheduled');
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [confirmDeletePkgId, setConfirmDeletePkgId] = useState(null);

  // Fire (on-demand) dialog state — separate from the edit dialog.
  const [fireOpen, setFireOpen] = useState(false);
  const [firingPkg, setFiringPkg] = useState(null);
  const [fireTasks, setFireTasks] = useState([]);
  const [fireSequential, setFireSequential] = useState(true);
  const [firing, setFiring] = useState(false);
  const isAdmin = user?.role === 'admin';

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pkgRes, usersRes, catsRes] = await Promise.all([
        api.get('/task-packages'), api.get('/users'), api.get('/categories'),
      ]);
      setPackages(pkgRes.data.packages);
      setUsers(usersRes.data.users);
      setCategories(catsRes.data.categories);
    } catch (err) { console.error('Failed to fetch:', err); }
  };

  const resetForm = () => { setPkgName(''); setIsActive(true); setFrequency('weekly'); setSequential(true); setStartDate(''); setTrigger('scheduled'); setTaskTemplates([]); setEditing(null); };
  const openCreate = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (pkg) => {
    setEditing(pkg); setPkgName(pkg.name); setIsActive(pkg.is_active);
    setFrequency(pkg.frequency || 'weekly');
    setSequential(pkg.sequential !== false);
    setStartDate(pkg.start_date ? pkg.start_date.slice(0, 10) : '');
    setTrigger(pkg.trigger || 'scheduled');
    setTaskTemplates((pkg.tasks || []).map(withUid)); setDialogOpen(true);
  };

  const addTemplate = () => {
    setTaskTemplates([...taskTemplates, withUid({ title: '', description: '', category_id: '', assigned_to: '', estimated_days: 7, slack_channel_id: '' })]);
  };
  const updateTemplate = (i, field, value) => {
    const updated = [...taskTemplates];
    updated[i] = { ...updated[i], [field]: value };
    setTaskTemplates(updated);
  };
  const removeTemplate = (i) => setTaskTemplates(taskTemplates.filter((_, idx) => idx !== i));
  const handleTemplateDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setTaskTemplates(prev => {
      const oldIndex = prev.findIndex(t => t.__uid === active.id);
      const newIndex = prev.findIndex(t => t.__uid === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };
  const handleFireDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFireTasks(prev => {
      const oldIndex = prev.findIndex(t => t.__uid === active.id);
      const newIndex = prev.findIndex(t => t.__uid === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSubmit = async () => {
    if (!pkgName.trim()) { toast.error('Package name required'); return; }
    if (taskTemplates.length === 0) { toast.error('Add at least one task template'); return; }
    if (taskTemplates.some(t => !t.title?.trim())) { toast.error('All tasks need a title'); return; }
    const payload = {
      name: pkgName.trim(), is_active: isActive, frequency,
      sequential,
      start_date: startDate || null,
      trigger,
      tasks: taskTemplates.map(t => ({
        title: t.title?.trim() || '', description: t.description?.trim() || '',
        category_id: t.category_id || null, assigned_to: t.assigned_to || null,
        estimated_days: parseInt(t.estimated_days) || 7,
        slack_channel_id: t.slack_channel_id?.trim() || null,
      })),
    };
    try {
      if (editing) {
        await api.put(`/task-packages/${editing.package_id}`, payload);
        toast.success('Package updated');
      } else {
        await api.post('/task-packages', payload);
        toast.success('Package created');
      }
      setDialogOpen(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handlePopulate = async (packageId) => {
    try {
      await api.post(`/task-packages/${packageId}/populate`);
      toast.success('Tasks populated from package'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to populate'); }
  };

  const openFire = (pkg) => {
    setFiringPkg(pkg);
    // Deep-clone the templates so edits in the fire dialog don't mutate the saved package.
    setFireTasks((pkg.tasks || []).map(t => withUid({ ...t })));
    setFireSequential(pkg.sequential !== false);
    setFireOpen(true);
  };
  const updateFireTask = (i, field, value) => {
    setFireTasks(prev => prev.map((t, idx) => idx === i ? { ...t, [field]: value } : t));
  };
  const addFireTask = () => {
    setFireTasks(prev => [...prev, withUid({ title: '', description: '', category_id: '', assigned_to: '', estimated_days: 7, slack_channel_id: '' })]);
  };
  const removeFireTask = (i) => setFireTasks(prev => prev.filter((_, idx) => idx !== i));

  const handleFire = async () => {
    if (fireTasks.length === 0) { toast.error('At least one task is required'); return; }
    if (fireTasks.some(t => !t.title?.trim())) { toast.error('All tasks need a title'); return; }
    setFiring(true);
    try {
      const payload = {
        sequential: fireSequential,
        tasks: fireTasks.map(t => ({
          title: t.title?.trim() || '', description: t.description?.trim() || '',
          category_id: t.category_id || null, assigned_to: t.assigned_to || null,
          estimated_days: parseInt(t.estimated_days) || 7,
          slack_channel_id: t.slack_channel_id?.trim() || null,
        })),
      };
      const res = await api.post(`/task-packages/${firingPkg.package_id}/fire`, payload);
      toast.success(res.data.message || 'Fired');
      setFireOpen(false); setFiringPkg(null); setFireTasks([]); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to fire'); }
    finally { setFiring(false); }
  };

  const handleDelete = (packageId) => {
    setConfirmDeletePkgId(packageId);
  };

  const confirmDelete = async () => {
    if (!confirmDeletePkgId) return;
    try {
      await api.delete(`/task-packages/${confirmDeletePkgId}`);
      toast.success('Package deleted'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    finally { setConfirmDeletePkgId(null); }
  };

  return (
    <div data-testid="packages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#34495e]">Task Packages</h2>
          <p className="text-sm text-[#7F8C8D]">Recurring task templates</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="create-package-btn">
            <Plus size={16} className="mr-1" /> New Package
          </Button>
        )}
      </div>

      <div className="grid gap-4" data-testid="packages-list">
        {packages.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-sm p-8 text-center text-[#BDC3C7] text-sm">
            No packages yet. Create one to auto-generate tasks every week.
          </div>
        ) : (
          packages.map(pkg => (
            <div key={pkg.package_id} className="bg-white border border-[#E2E8F0] rounded-sm p-4" data-testid={`package-card-${pkg.package_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-bold text-[#34495e]">{pkg.name}</h3>
                  <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold ${
                    pkg.is_active ? 'bg-[#27ae60] text-white' : 'bg-[#E2E8F0] text-[#7F8C8D]'
                  }`}>
                    {pkg.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold bg-[#f39c12] text-white capitalize">
                    {pkg.trigger === 'on_demand' ? 'On-Demand' : (pkg.frequency || 'weekly')}
                  </span>
                  <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold ${
                    pkg.sequential === false ? 'bg-[#3498db] text-white' : 'bg-[#34495e] text-white'
                  }`} data-testid={`package-mode-${pkg.package_id}`}>
                    {pkg.sequential === false ? 'Parallel' : 'Sequential'}
                  </span>
                  {pkg.start_date && (
                    <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold bg-[#9b59b6] text-white" data-testid={`package-start-date-${pkg.package_id}`}>
                      Starts {new Date(pkg.start_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex gap-1 items-center">
                    {pkg.trigger === 'on_demand' ? (
                      <Button variant="outline" size="sm" onClick={() => openFire(pkg)}
                        className="rounded-sm border-[#3498db] text-[#3498db] hover:bg-[#3498db] hover:text-white" data-testid={`fire-pkg-${pkg.package_id}`}>
                        <Play size={14} className="mr-1" /> Fire
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => handlePopulate(pkg.package_id)}
                        className="rounded-sm border-[#27ae60] text-[#27ae60] hover:bg-[#27ae60] hover:text-white" data-testid={`populate-pkg-${pkg.package_id}`}>
                        <Play size={14} className="mr-1" /> Populate Now
                      </Button>
                    )}
                    <button onClick={() => openEdit(pkg)} className="p-1.5 text-[#7F8C8D] hover:text-[#34495e]" data-testid={`edit-pkg-${pkg.package_id}`}><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(pkg.package_id)} className="p-1.5 text-[#7F8C8D] hover:text-[#e74c3c]" data-testid={`delete-pkg-${pkg.package_id}`}><Trash2 size={14} /></button>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#7F8C8D] mb-2">
                {pkg.tasks?.length || 0} tasks | Last populated: {pkg.last_populated_at ? new Date(pkg.last_populated_at).toLocaleDateString() : 'Never'}
              </p>
              <div className="flex flex-wrap gap-1">
                {(pkg.tasks || []).map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-[10px] text-[#34495e]">
                    <span className="font-bold text-[#7F8C8D]">{i + 1}.</span> {t.title}
                  </span>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-lg bg-white border border-[#E2E8F0] rounded-sm max-h-[80vh] overflow-y-auto" data-testid="package-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight text-[#34495e]">{editing ? 'Edit Package' : 'New Package'}</DialogTitle>
            <DialogDescription className="sr-only">{editing ? 'Edit package details' : 'Create a new task package'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Package Name</Label>
              <Input value={pkgName} onChange={(e) => setPkgName(e.target.value)} placeholder="e.g. Weekly Sprint Tasks" className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="package-name-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={isActive} onCheckedChange={setIsActive} data-testid="package-active-switch" />
              <Label className="text-sm text-[#34495e]">Active (auto-populates on schedule)</Label>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Trigger</Label>
              <Select value={trigger} onValueChange={setTrigger}>
                <SelectTrigger className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="package-trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled (auto on frequency)</SelectItem>
                  <SelectItem value="on_demand">On-Demand (fire manually, edit before firing)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#BDC3C7] mt-1">
                {trigger === 'on_demand'
                  ? 'On-demand: you hit "Fire", edit the tasks in a preview, then create them. Great for events like "New Model Update".'
                  : 'Scheduled: tasks populate automatically on the chosen frequency.'}
              </p>
            </div>
            {trigger === 'scheduled' && (
              <>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Frequency</Label>
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger className="mt-1 rounded-sm border-[#E2E8F0]" data-testid="package-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Start Date (optional)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="rounded-sm border-[#E2E8F0] w-auto"
                      data-testid="package-start-date-input"
                    />
                    {startDate && (
                      <Button variant="ghost" size="sm" onClick={() => setStartDate('')} className="h-8 px-2 text-xs text-[#7F8C8D] rounded-sm" data-testid="package-start-date-clear-btn">
                        Clear
                      </Button>
                    )}
                  </div>
                  <p className="text-[10px] text-[#BDC3C7] mt-1">
                    If set, the package is held until this date and then populates on its normal frequency.
                  </p>
                </div>
              </>
            )}
            <div className="border border-[#E2E8F0] rounded-sm p-3 bg-[#F8F9FA]">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Task Mode</Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${!sequential ? 'text-[#34495e] font-bold' : 'text-[#BDC3C7]'}`}>Parallel</span>
                  <Switch checked={sequential} onCheckedChange={setSequential} data-testid="package-sequential-switch" />
                  <span className={`text-xs ${sequential ? 'text-[#34495e] font-bold' : 'text-[#BDC3C7]'}`}>Sequential</span>
                </div>
              </div>
              <p className="text-[10px] text-[#BDC3C7] mt-2">
                {sequential
                  ? 'Sequential: tasks unlock one at a time — each task must be Done before the next becomes available.'
                  : 'Parallel: all tasks populate unlocked at once; assignees can work on any of them in any order.'}
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Task Templates</Label>
                <Button variant="outline" size="sm" onClick={addTemplate} className="rounded-sm border-[#E2E8F0] text-[#34495e]" data-testid="add-template-btn">
                  <Plus size={14} className="mr-1" /> Add Task
                </Button>
              </div>
              <div className="space-y-3">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTemplateDragEnd}>
                  <SortableContext items={taskTemplates.map(t => t.__uid)} strategy={verticalListSortingStrategy}>
                    {taskTemplates.map((t, i) => (
                      <SortableItem key={t.__uid} id={t.__uid} testId={`template-drag-${i}`}>
                        <div className="border border-[#E2E8F0] rounded-sm p-3 bg-[#F8F9FA] mb-3">
                          <div className="flex items-start gap-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6 rounded-sm bg-[#34495e] text-white text-[10px] font-bold shrink-0 mt-1">{i + 1}</span>
                            <Input value={t.title || ''} onChange={(e) => updateTemplate(i, 'title', e.target.value)} placeholder="Task title"
                              className="flex-1 rounded-sm border-[#E2E8F0] bg-white text-sm" data-testid={`template-title-${i}`} />
                            <button onClick={() => removeTemplate(i)} className="p-1 text-[#7F8C8D] hover:text-[#e74c3c]" data-testid={`remove-template-${i}`}><X size={14} /></button>
                          </div>
                          {i === 0 && sequential && taskTemplates.length > 1 && (
                            <p className="text-[9px] text-[#27ae60] font-bold uppercase mb-2 ml-8">Starts first — subsequent tasks are locked until the previous one is done</p>
                          )}
                          <Input value={t.description || ''} onChange={(e) => updateTemplate(i, 'description', e.target.value)} placeholder="Description (optional)"
                            className="mb-2 rounded-sm border-[#E2E8F0] bg-white text-xs h-8" data-testid={`template-desc-${i}`} />
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            <div>
                              <span className="text-[9px] text-[#7F8C8D] font-bold uppercase">Category</span>
                              <Select value={t.category_id || 'none'} onValueChange={(v) => updateTemplate(i, 'category_id', v === 'none' ? '' : v)}>
                                <SelectTrigger className="h-8 text-xs rounded-sm border-[#E2E8F0] bg-white"><SelectValue placeholder="Category" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <span className="text-[9px] text-[#7F8C8D] font-bold uppercase">Assign to</span>
                              <Select value={t.assigned_to || 'none'} onValueChange={(v) => updateTemplate(i, 'assigned_to', v === 'none' ? '' : v)}>
                                <SelectTrigger className="h-8 text-xs rounded-sm border-[#E2E8F0] bg-white"><SelectValue placeholder="Assign to" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Unassigned</SelectItem>
                                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <span className="text-[9px] text-[#7F8C8D] font-bold uppercase">Est. Days</span>
                              <Input type="number" value={t.estimated_days || 7} onChange={(e) => updateTemplate(i, 'estimated_days', e.target.value)}
                                placeholder="7" className="h-8 text-xs rounded-sm border-[#E2E8F0] bg-white" min="1" data-testid={`template-days-${i}`} />
                            </div>
                          </div>
                          <SlackChannelPicker
                            value={t.slack_channel_id || ''}
                            onChange={(val) => updateTemplate(i, 'slack_channel_id', val)}
                            placeholder="Slack channel on completion (optional)"
                            testId={`template-slack-channel-${i}`}
                          />
                        </div>
                      </SortableItem>
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
            <Button onClick={handleSubmit} className="w-full bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="package-submit-btn">
              {editing ? 'Update Package' : 'Create Package'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fire (on-demand) Dialog */}
      <Dialog open={fireOpen} onOpenChange={(open) => { if (!open) { setFireOpen(false); setFiringPkg(null); } }}>
        <DialogContent className="max-w-lg bg-white border border-[#E2E8F0] rounded-sm max-h-[80vh] overflow-y-auto" data-testid="fire-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight text-[#34495e]">Fire: {firingPkg?.name}</DialogTitle>
            <DialogDescription className="text-xs text-[#7F8C8D]">
              Edit / add / remove tasks below, then create this batch. The saved package template is not modified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="border border-[#E2E8F0] rounded-sm p-3 bg-[#F8F9FA]">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Task Mode (for this fire only)</Label>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${!fireSequential ? 'text-[#34495e] font-bold' : 'text-[#BDC3C7]'}`}>Parallel</span>
                  <Switch checked={fireSequential} onCheckedChange={setFireSequential} data-testid="fire-sequential-switch" />
                  <span className={`text-xs ${fireSequential ? 'text-[#34495e] font-bold' : 'text-[#BDC3C7]'}`}>Sequential</span>
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Tasks to Create</Label>
                <Button variant="outline" size="sm" onClick={addFireTask} className="rounded-sm border-[#E2E8F0] text-[#34495e]" data-testid="fire-add-task-btn">
                  <Plus size={14} className="mr-1" /> Add Task
                </Button>
              </div>
              <div className="space-y-3">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFireDragEnd}>
                  <SortableContext items={fireTasks.map(t => t.__uid)} strategy={verticalListSortingStrategy}>
                    {fireTasks.map((t, i) => (
                      <SortableItem key={t.__uid} id={t.__uid} testId={`fire-drag-${i}`}>
                        <div className="border border-[#E2E8F0] rounded-sm p-3 bg-[#F8F9FA] mb-3">
                          <div className="flex items-start gap-2 mb-2">
                            <span className="flex items-center justify-center w-6 h-6 rounded-sm bg-[#3498db] text-white text-[10px] font-bold shrink-0 mt-1">{i + 1}</span>
                            <Input value={t.title || ''} onChange={(e) => updateFireTask(i, 'title', e.target.value)} placeholder="Task title"
                              className="flex-1 rounded-sm border-[#E2E8F0] bg-white text-sm" data-testid={`fire-task-title-${i}`} />
                            <button onClick={() => removeFireTask(i)} className="p-1 text-[#7F8C8D] hover:text-[#e74c3c]" data-testid={`fire-remove-task-${i}`}><X size={14} /></button>
                          </div>
                          <Input value={t.description || ''} onChange={(e) => updateFireTask(i, 'description', e.target.value)} placeholder="Description (optional)"
                            className="mb-2 rounded-sm border-[#E2E8F0] bg-white text-xs h-8" data-testid={`fire-task-desc-${i}`} />
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            <div>
                              <Select value={t.category_id || ''} onValueChange={(v) => updateFireTask(i, 'category_id', v === '__none__' ? '' : v)}>
                                <SelectTrigger className="rounded-sm border-[#E2E8F0] bg-white text-xs h-8"><SelectValue placeholder="Category" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">None</SelectItem>
                                  {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Select value={t.assigned_to || ''} onValueChange={(v) => updateFireTask(i, 'assigned_to', v === '__none__' ? '' : v)}>
                                <SelectTrigger className="rounded-sm border-[#E2E8F0] bg-white text-xs h-8"><SelectValue placeholder="Assignee" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none__">Unassigned</SelectItem>
                                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <Input type="number" min="1" value={t.estimated_days || 7} onChange={(e) => updateFireTask(i, 'estimated_days', e.target.value)}
                              className="rounded-sm border-[#E2E8F0] bg-white text-xs h-8" placeholder="Days" data-testid={`fire-task-days-${i}`} />
                          </div>
                          <SlackChannelPicker
                            value={t.slack_channel_id || ''}
                            onChange={(val) => updateFireTask(i, 'slack_channel_id', val)}
                            placeholder="Slack channel on completion (optional)"
                            testId={`fire-task-slack-${i}`}
                          />
                        </div>
                      </SortableItem>
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
            <Button onClick={handleFire} disabled={firing} className="w-full bg-[#3498db] hover:bg-[#2980b9] text-white rounded-sm" data-testid="fire-submit-btn">
              <Play size={14} className="mr-1.5" /> {firing ? 'Firing...' : `Create ${fireTasks.length} Task${fireTasks.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Package */}
      <AlertDialog open={!!confirmDeletePkgId} onOpenChange={(open) => { if (!open) setConfirmDeletePkgId(null); }}>
        <AlertDialogContent className="bg-white border border-[#E2E8F0] rounded-sm" data-testid="confirm-delete-pkg-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-black text-[#34495e]">Delete this package?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#7F8C8D]">
              This action cannot be undone. The package template will be permanently removed. Existing tasks created from this package will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-[#E2E8F0] text-[#7F8C8D]" data-testid="confirm-delete-pkg-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-[#e74c3c] hover:bg-[#c0392b] text-white" data-testid="confirm-delete-pkg-yes">Yes, delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
